import { useState } from "react";
import { useMutation } from "@apollo/client";
import { GET_ACTIVE_USER, REGISTER_USER } from "../../../queries/auth.queries";
import {
  RegisterResponse,
  RegisterVariables,
} from "../../../queries/auth.queries.types";
import { useAuthErrorAlert } from "../useAuthErrorAlert";
import { RegisterFormValues } from "./RegisterForm.types";

export const useRegisterForm = () => {
  const showError = useAuthErrorAlert();
  const [registrationComplete, setRegistrationComplete] = useState(false);

  const [register] = useMutation<RegisterResponse, RegisterVariables>(
    REGISTER_USER,
    {
      refetchQueries: [{ query: GET_ACTIVE_USER }],
    }
  );

  const onSubmit = async (values: RegisterFormValues) => {
    try {
      const res = await register({
        variables: { emailAddress: values.email, password: values.password },
      });
      const registerResult = res.data?.registerCustomerAccount;

      if (registerResult?.__typename !== "Success") {
        showError(registerResult?.message);
        return;
      }

      // `Success` is deliberately ambiguous by design: Vendure returns it
      // both for a genuinely new registration AND for an email that's
      // already registered, specifically to prevent email-enumeration
      // attacks (a client can't distinguish "new account" from "someone
      // already has this email" from the response). We can't log the user
      // in here — the password they just typed isn't necessarily the real
      // account's password if it already existed — so the only honest,
      // safe response for either case is the same generic "check your
      // email" message.
      setRegistrationComplete(true);
    } catch (err) {
      showError((err as Error)?.message);
    }
  };

  return {
    onSubmit,
    registrationComplete,
  };
};
